import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { subMenuOpen, subMenuClose } from "../Actions/subMenuOpenClose";
import themeColors from "../../data/colors";

gsap.registerPlugin(ScrollTrigger);

export function initScrollControl({
  containerRef,
  navRefs,
  indicatorRef,
  submenuTimelines,
  allVideoPausedRef,
  setActiveSection,
}) {
  ScrollTrigger.getAll().forEach((st) => st.kill());

  const moveIndicatorToSlug = (slug, color) => {
    const indicator = indicatorRef.current;
    const navUl = document.querySelector(".nav-ul");
    const navItem =
      navRefs.current?.[slug] || document.getElementById(`nav-${slug}`);
    if (!indicator || !navUl || !navItem) return;

    const y =
      navItem.getBoundingClientRect().top - navUl.getBoundingClientRect().top;

    gsap.to(indicator, {
      y,
      ...(color ? { backgroundColor: color } : {}),
      duration: 0.5,
      ease: "power3.out",
      overwrite: "auto",
    });
  };

  let navAnimationTimeout = null;
  const panels = gsap.utils.toArray(".panel", containerRef.current);

  // panel.id -> color
  const idToColor = new Map();
  panels.forEach((panel, i) => {
    idToColor.set(
      panel.id,
      themeColors[i]?.light.hex || themeColors[0]?.light.hex,
    );
  });

  const handleNavAnimation = (id) => {
    clearTimeout(navAnimationTimeout);
    navAnimationTimeout = setTimeout(() => {
      setActiveSection(id);

      Object.entries(navRefs.current).forEach(([key, navItem]) => {
        if (!navItem) return;
        const submenu = navItem.querySelector(".submenu");
        const submenuItems = navItem.querySelectorAll(".submenu-item");
        if (!submenu || !submenuItems) return;

        if (key === id) {
          // OPEN: move indicator only when open completes
          subMenuOpen(submenu, submenuItems, submenuTimelines, {
            onIndicator: () => {
              moveIndicatorToSlug(id, idToColor.get(id));
            },
            onDone: () => {
              ScrollTrigger.refresh();
              moveIndicatorToSlug(id, idToColor.get(id));
            },
          });
        } else {
          // CLOSE: indicator does nothing (for now)
          subMenuClose(submenu, submenuItems, submenuTimelines);
        }
      });
    }, 0);
  };

  // Keep: panel scroll triggers for opening/closing submenus + active section
  panels.forEach((panel, i) => {
    const child = panel.querySelector(".video-container");

    if (child) {
      // Known baseline (prevents “already blurred” from CSS/previous runs)
      gsap.set(child, { yPercent: 0, filter: "blur(0px)", autoAlpha: 1 });

      gsap.fromTo(
        child,
        { yPercent: 0, filter: "blur(0px)", autoAlpha: 1 },
        {
          yPercent: 20,
          filter: "blur(10px)",
          autoAlpha: 0.25,
          ease: "none",
          scrollTrigger: {
            trigger: panel,
            start: "top top",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,

            // Optional but helpful if layout shifts are happening:
            onRefreshInit: () =>
              gsap.set(child, {
                yPercent: 0,
                filter: "blur(0px)",
                autoAlpha: 1,
              }),

            // markers: true, // turn on briefly to confirm start/end are where you expect
          },
        },
      );
    }

    ScrollTrigger.create({
      trigger: panel,
      start: "top 20%",
      end: "bottom 80%",
      scrub: true,
      onEnter: () => handleNavAnimation(panel.id),
      onEnterBack: () => handleNavAnimation(panel.id),
    });

    // REMOVE the old indicator ScrollTrigger entirely

    const video = panel.querySelector("video");
    if (video) {
      ScrollTrigger.create({
        trigger: panel,
        start: "top 99%",
        end: "bottom 1%",
        onEnter: () => {
          if (!allVideoPausedRef.current) video.play().catch(() => {});
        },
        onEnterBack: () => {
          if (!allVideoPausedRef.current) video.play().catch(() => {});
        },
        onLeave: () => video.pause(),
        onLeaveBack: () => video.pause(),
      });
    }
  });
}
